import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, themeQuartz } from 'ag-grid-community';
import { TmsTrainingDataService } from '../../../../../data-services/tms-training-data.service';
import { UserSessionService } from '../../../../../ui-framework/global-services/user-session/user-session.service';
import { TrainingPlanStatus } from '../../../../../data-services/api-models/shared-models';

export interface OfficeSummaryItem {
  officeId?: string;
  officeName?: string;
  scopeName: string;
  treeLevel?: number;
  isOwnOffice?: boolean;
  totalPlans: number;
  draftCount: number;
  approvedCount: number;
  inProgressCount: number;
  completedCount: number;
  totalTrainees: number;
  totalPlanExpense: number;
  totalProjectedExpense: number;
}

export interface TrainingPlanItem {
  planId: string;
  planCode: string;
  planDescription: string;
  trainingName: string;
  officeName: string;
  districtName?: string;
  treeLevel: number;
  officeScope: string;
  isOwnOffice: boolean;
  statusId: number;
  planStatus: string;
  startDate?: string;
  endDate?: string;
  trainingDuration: number;
  totalNoOfTrainee: number;
  planExpense: number;
  projectedExpense: number;
}

@Component({
  selector: 'app-training-plan-hierarchy-summary',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatTabsModule,
    MatBadgeModule,
    MatButtonModule,
    AgGridAngular
  ],
  templateUrl: './training-plan-hierarchy-summary.component.html',
  styleUrl: './training-plan-hierarchy-summary.component.scss',
  providers: [TmsTrainingDataService]
})
export class TrainingPlanHierarchySummaryComponent implements OnInit {
  readonly theme = themeQuartz;
  readonly defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  readonly officeWiseColumnDefs: ColDef<OfficeSummaryItem>[] = [
    {
      headerName: 'Office Name',
      valueGetter: params => params.data?.officeName || params.data?.scopeName || '',
      minWidth: 240,
      flex: 1,
      cellClass: 'font-bold',
    },
    {
      headerName: 'Hierarchy Scope',
      minWidth: 160,
      valueGetter: params => params.data ? (params.data.isOwnOffice ? 'Own Office' : 'Sub Office (Level ' + (params.data.treeLevel ?? 0) + ')') : '',
    },
    {
      headerName: 'Total Plans',
      field: 'totalPlans',
      width: 120,
      cellClass: 'text-center font-semibold',
    },
    {
      headerName: 'Approved',
      field: 'approvedCount',
      width: 110,
      cellClass: 'text-center font-semibold text-success',
    },
    {
      headerName: 'In-Progress',
      field: 'inProgressCount',
      width: 120,
      cellClass: 'text-center font-semibold text-warning',
    },
    {
      headerName: 'Completed',
      field: 'completedCount',
      width: 120,
      cellClass: 'text-center font-semibold text-info',
    },
    {
      headerName: 'Total Trainees',
      field: 'totalTrainees',
      minWidth: 140,
      valueFormatter: params => params.value != null ? params.value.toLocaleString() + ' Attendees' : '0 Attendees',
    },
    {
      headerName: 'Plan Expense',
      field: 'totalPlanExpense',
      minWidth: 140,
      valueFormatter: params => params.value != null ? '₹' + Number(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '₹0.00',
    },
    {
      headerName: 'Projected Expense',
      field: 'totalProjectedExpense',
      minWidth: 160,
      valueFormatter: params => params.value != null ? '₹' + Number(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '₹0.00',
    }
  ];

  readonly planColumnDefs: ColDef<TrainingPlanItem>[] = [
    {
      headerName: 'Plan Code',
      field: 'planCode',
      width: 130,
      cellClass: 'font-bold',
    },
    {
      headerName: 'Training Name',
      minWidth: 260,
      flex: 1,
      cellClass: 'text-wrap-cell',
      cellRenderer: (params: any) => {
        if (!params.data) return '';
        const name = params.data.trainingName || '';
        const desc = params.data.planDescription || '';
        return `<div><div style="font-weight:600;color:#0f172a;">${name}</div><div style="font-size:0.75rem;color:#64748b;line-height:1.2;">${desc}</div></div>`;
      }
    },
    {
      headerName: 'Office Name',
      field: 'officeName',
      minWidth: 200,
      cellClass: 'text-wrap-cell',
    },
    {
      headerName: 'Duration',
      field: 'trainingDuration',
      width: 100,
      valueFormatter: params => params.value ? params.value + ' Days' : '',
    },
    {
      headerName: 'Trainees',
      field: 'totalNoOfTrainee',
      width: 130,
      valueFormatter: params => params.value != null ? params.value.toLocaleString() + ' Attendees' : '0 Attendees',
    },
    {
      headerName: 'Plan Expense',
      field: 'planExpense',
      minWidth: 130,
      valueFormatter: params => params.value != null ? '₹' + Number(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '₹0.00',
    },
    {
      headerName: 'Status',
      field: 'planStatus',
      width: 130,
      cellClass: 'text-center',
      cellRenderer: (params: any) => {
        if (!params.data) return '';
        const statusId = params.data.statusId;
        const statusText = params.value || 'Draft';
        let statusClass = 'status-active';

        if (statusId === TrainingPlanStatus.ApprovalInProgress) {
          statusClass = 'status-warning';
        } else if (statusId === TrainingPlanStatus.OnHold) {
          statusClass = 'status-info';
        } else if (statusId === TrainingPlanStatus.Return) {
          statusClass = 'status-danger';
        }

        return `<div class="grid-status-badge ${statusClass}"><span class="badge-dot"></span>${statusText}</div>`;
      }
    }
  ];

  readonly ownOfficeSummary = signal<OfficeSummaryItem>({
    scopeName: 'Own Office Plan Summary',
    totalPlans: 0,
    draftCount: 0,
    approvedCount: 0,
    inProgressCount: 0,
    completedCount: 0,
    totalTrainees: 0,
    totalPlanExpense: 0,
    totalProjectedExpense: 0
  });

  readonly subOfficeSummary = signal<OfficeSummaryItem>({
    scopeName: 'Sub Office Plan Summary',
    totalPlans: 0,
    draftCount: 0,
    approvedCount: 0,
    inProgressCount: 0,
    completedCount: 0,
    totalTrainees: 0,
    totalPlanExpense: 0,
    totalProjectedExpense: 0
  });

  readonly officeWiseSummaries = signal<OfficeSummaryItem[]>([]);
  readonly ownOfficePlans = signal<TrainingPlanItem[]>([]);
  readonly subOfficePlans = signal<TrainingPlanItem[]>([]);
  readonly selectedOfficeFilter = signal<string>('all');
  readonly isLoading = signal<boolean>(true);
  readonly errorMessage = signal<string | null>(null);
  readonly activeTab = signal<'officewise' | 'own' | 'sub'>('officewise');

  constructor(
    private readonly trainingDataService: TmsTrainingDataService,
    private readonly userSessionService: UserSessionService
  ) {}

  ngOnInit(): void {
    this.loadHierarchySummary();
  }

  loadHierarchySummary(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.trainingDataService.getTrainingPlanHierarchySummary().subscribe({
      next: (response: any) => {
        this.isLoading.set(false);
        if (response) {
          if (response.ownOfficeSummary) {
            this.ownOfficeSummary.set(response.ownOfficeSummary);
          }
          if (Array.isArray(response.officeWiseSummaries)) {
            this.officeWiseSummaries.set(response.officeWiseSummaries);
            // Calculate total sub-office summary across all child offices if not provided explicitly by API
            if (!response.subOfficeSummary) {
              this.subOfficeSummary.set(this.calculateSubOfficeSummary(response.officeWiseSummaries));
            } else {
              this.subOfficeSummary.set(response.subOfficeSummary);
            }
          } else if (response.subOfficeSummary) {
            this.subOfficeSummary.set(response.subOfficeSummary);
          }
          if (Array.isArray(response.details)) {
            this.ownOfficePlans.set(response.details.filter((item: any) => item.isOwnOffice));
            this.subOfficePlans.set(response.details.filter((item: any) => !item.isOwnOffice));
          }
        }
      },
      error: (err: any) => {
        console.error('Error fetching hierarchy training plan summary:', err);
        this.isLoading.set(false);
        this.loadMockData();
      }
    });
  }

  private calculateSubOfficeSummary(summaries: OfficeSummaryItem[]): OfficeSummaryItem {
    const childOffices = summaries.filter(item => !item.isOwnOffice);
    return {
      scopeName: 'Sub Office Plan Summary (All Child Offices)',
      totalPlans: childOffices.reduce((acc, curr) => acc + (curr.totalPlans || 0), 0),
      draftCount: childOffices.reduce((acc, curr) => acc + (curr.draftCount || 0), 0),
      approvedCount: childOffices.reduce((acc, curr) => acc + (curr.approvedCount || 0), 0),
      inProgressCount: childOffices.reduce((acc, curr) => acc + (curr.inProgressCount || 0), 0),
      completedCount: childOffices.reduce((acc, curr) => acc + (curr.completedCount || 0), 0),
      totalTrainees: childOffices.reduce((acc, curr) => acc + (curr.totalTrainees || 0), 0),
      totalPlanExpense: childOffices.reduce((acc, curr) => acc + (curr.totalPlanExpense || 0), 0),
      totalProjectedExpense: childOffices.reduce((acc, curr) => acc + (curr.totalProjectedExpense || 0), 0),
    };
  }

  private loadMockData(): void {
    const mockOwn: OfficeSummaryItem = {
      scopeName: 'Own Office Plan Summary',
      totalPlans: 4,
      draftCount: 1,
      approvedCount: 1,
      inProgressCount: 1,
      completedCount: 1,
      totalTrainees: 120,
      totalPlanExpense: 45000,
      totalProjectedExpense: 42000
    };

    const mockSummaries: OfficeSummaryItem[] = [
      {
        officeId: 'off-1',
        officeName: 'Jhargram District Office (DMMU)',
        scopeName: 'Own Office',
        treeLevel: 0,
        isOwnOffice: true,
        totalPlans: 4,
        draftCount: 1,
        approvedCount: 1,
        inProgressCount: 1,
        completedCount: 1,
        totalTrainees: 120,
        totalPlanExpense: 45000,
        totalProjectedExpense: 42000
      },
      {
        officeId: 'off-2',
        officeName: 'Binpur Two Block Office (BMMU)',
        scopeName: 'Sub Office (Block)',
        treeLevel: 1,
        isOwnOffice: false,
        totalPlans: 5,
        draftCount: 0,
        approvedCount: 2,
        inProgressCount: 2,
        completedCount: 1,
        totalTrainees: 150,
        totalPlanExpense: 60000,
        totalProjectedExpense: 55000
      },
      {
        officeId: 'off-3',
        officeName: 'Bhulaveda Mahila Unnayan Sangha (CLF)',
        scopeName: 'Sub Office (CLF)',
        treeLevel: 2,
        isOwnOffice: false,
        totalPlans: 5,
        draftCount: 1,
        approvedCount: 2,
        inProgressCount: 1,
        completedCount: 1,
        totalTrainees: 140,
        totalPlanExpense: 58000,
        totalProjectedExpense: 54000
      },
      {
        officeId: 'off-4',
        officeName: 'Dahijuri Mahila Sangha (CLF)',
        scopeName: 'Sub Office (CLF)',
        treeLevel: 2,
        isOwnOffice: false,
        totalPlans: 4,
        draftCount: 1,
        approvedCount: 1,
        inProgressCount: 1,
        completedCount: 1,
        totalTrainees: 110,
        totalPlanExpense: 47000,
        totalProjectedExpense: 43000
      }
    ];

    this.ownOfficeSummary.set(mockOwn);
    this.officeWiseSummaries.set(mockSummaries);
    this.subOfficeSummary.set(this.calculateSubOfficeSummary(mockSummaries));

    this.ownOfficePlans.set([
      {
        planId: '1',
        planCode: 'TP-DIST-001',
        planDescription: 'District Level Capacity Building & Planning',
        trainingName: 'Leadership & SHG Management',
        officeName: 'Jhargram District Office (DMMU)',
        treeLevel: 0,
        officeScope: 'Own Office',
        isOwnOffice: true,
        statusId: 30,
        planStatus: 'Training Approved',
        trainingDuration: 3,
        totalNoOfTrainee: 30,
        planExpense: 15000,
        projectedExpense: 14000
      },
      {
        planId: '2',
        planCode: 'TP-DIST-002',
        planDescription: 'District Financial Literacy Workshop',
        trainingName: 'Bookkeeping & Accounts',
        officeName: 'Jhargram District Office (DMMU)',
        treeLevel: 0,
        officeScope: 'Own Office',
        isOwnOffice: true,
        statusId: 70,
        planStatus: 'Training In-Progress',
        trainingDuration: 2,
        totalNoOfTrainee: 25,
        planExpense: 10000,
        projectedExpense: 9500
      }
    ]);

    this.subOfficePlans.set([
      {
        planId: '3',
        planCode: 'TP-BLOCK-101',
        planDescription: 'Block Level Skill Enhancement Workshop',
        trainingName: 'Field Level Facilitation Skills',
        officeName: 'Binpur Two Block Office (BMMU)',
        treeLevel: 1,
        officeScope: 'Sub Office (Block)',
        isOwnOffice: false,
        statusId: 30,
        planStatus: 'Training Approved',
        trainingDuration: 3,
        totalNoOfTrainee: 45,
        planExpense: 22000,
        projectedExpense: 20000
      },
      {
        planId: '4',
        planCode: 'TP-CLF-201',
        planDescription: 'Cluster Sangha Training Session',
        trainingName: 'Community Mobilization & Micro-Finance',
        officeName: 'Bhulaveda Mahila Unnayan Sangha (CLF)',
        treeLevel: 2,
        officeScope: 'Sub Office (CLF)',
        isOwnOffice: false,
        statusId: 80,
        planStatus: 'Training Completed',
        trainingDuration: 2,
        totalNoOfTrainee: 40,
        planExpense: 20000,
        projectedExpense: 19000
      },
      {
        planId: '5',
        planCode: 'TP-CLF-202',
        planDescription: 'Micro-Enterprise Skill Training',
        trainingName: 'Livelihood & Small Enterprise',
        officeName: 'Dahijuri Mahila Sangha (CLF)',
        treeLevel: 2,
        officeScope: 'Sub Office (CLF)',
        isOwnOffice: false,
        statusId: 30,
        planStatus: 'Training Approved',
        trainingDuration: 4,
        totalNoOfTrainee: 50,
        planExpense: 28000,
        projectedExpense: 26000
      }
    ]);
  }

  getStatusClass(statusId: number): string {
    // Maps to .status-pill modifier classes defined in component SCSS
    switch (statusId) {
      case 30: return 'approved';    // Approved / Training Approved
      case 70: return 'inprogress';  // In-Progress
      case 80: return 'completed';   // Completed
      case 90: return 'rejected';    // Rejected / Cancelled
      default: return '';            // Default (grey)
    }
  }
}
